
import { defineComponent } from "vue";
import { reportsActions, reportsGetters } from "@/store/reports/types";
import { mapActions, mapGetters, mapState } from "vuex";
import { useI18n } from "vue-i18n";
import { RootActions } from "@/store/actions";
import IModal from "@/components/modal/IModal.vue";
import { ISlide } from "@/models/slide";
import { RootGetters } from "@/store/getters";
import { TextArea } from "@progress/kendo-vue-inputs";
import { debounce } from "@/core/utils";
import IButton from "@/components/buttons/IButton.vue";
import WorkflowStateButton from "@/components/header/WorkflowStateButton.vue";
import SlideReport from "@/modules/reports/case/slides/SlideReport.vue";
import CaseReportObservations from "@/modules/reports/case/observations/CaseReportObservations.vue";
import { PDFExport } from "@progress/kendo-vue-pdf";
import { productName } from "@/models/system-mode";
import IBTooltip from "@/components/tooltip/IBTooltip.vue";
import IbIcons from "@/components/icons/ib-icons.vue";

export default defineComponent({
  name: "CaseReport",
  setup() {
    const { t } = useI18n({
      inheritLocale: true,
      useScope: "local",
    });
    return { t };
  },
  data() {
    return {
      isFormValid: true,
      copyToClipboardText: "reports.actionButtonText.copyToClipboard",
      isCopied: false,
      isLoading: true,
    };
  },
  components: {
    IModal,
    SlideReport,
    IButton,
    WorkflowStateButton,
    KTextarea: TextArea,
    CaseReportObservations,
    pdfexport: PDFExport,
    IBTooltip,
    IbIcons,
  },
  mounted() {
    setTimeout(() => {
      this.isLoading = false;
      this.checkValidity();
    }, this.config.case_report_loader_duration_ms);
  },
  computed: {
    ...mapState(["activeCaseId", "loggedInUserName", "insightsConfig"]),
    ...mapGetters({
      config: RootGetters.CONFIG,
      slides: RootGetters.SLIDES,
      activeCase: RootGetters.GET_ACTIVE_CASE,
      isCaseCompletionEnabled: RootGetters.GET_IS_CASE_COMPLETION_ENABLED,
      isCaseComplete: RootGetters.GET_IS_CASE_COMPLETE,
      systemMode: RootGetters.GET_SYSTEM_MODE,
      globals: RootGetters.GET_GLOBALS,
    }),
    ...mapGetters("reportsModule", {
      caseReport: reportsGetters.ACTIVE_CASE_REPORT,
      hasCaseReport: reportsGetters.HAS_CASE_REPORT,
      filledSlideObservations: reportsGetters.FILLED_SLIDE_OBSERVATIONS,
      caseComments: reportsGetters.CASE_COMMENTS,
      slideComments: reportsGetters.SLIDE_COMMENTS,
      caseReportComplete: reportsGetters.CASE_REPORT_COMPLETE,
      completedBy: reportsGetters.COMPLETED_BY,
      completedOn: reportsGetters.COMPLETED_ON,
    }),
    displayFindingsInPDF() {
      return this.config.enable_case_report_pdf_findings;
    },
    caseReportComments: {
      get() {
        return this.caseComments(this.activeCaseId);
      },
      set(value: string) {
        debounce(
          () =>
            this.updateCaseComments({
              caseId: this.activeCaseId,
              comments: value,
            }),
          1000
        );
      },
    },
    workflowStateButtonTitle() {
      if (!this.isCaseCompletionEnabled && !this.isFormValid) {
        return "workflowStateButton.title.incompleteAndRequired";
      }
      if (!this.isCaseCompletionEnabled) {
        return "workflowStateButton.title.incomplete";
      }
      if (!this.isFormValid) {
        return "workflowStateButton.title.required";
      }
      return "";
    },
    computedComments() {
      const map = {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        "\"": "&quot;"
      };
      return this.caseReportComments
        .replaceAll(/[&<>"]/g, function(m: string) { return map[m]; })
        .replaceAll('\n', '<br/>')
    }
  },
  methods: {
    ...mapActions({
      closeModal: RootActions.RESET_ACTIVE_VIEW,
      navigateToSlide: RootActions.NAVIGATE_TO_SLIDE,
    }),
    ...mapActions("reportsModule", {
      updateCaseComments: reportsActions.UPDATE_CASE_COMMENTS,
    }),
    getObservations(slideId: string) {
      return this.filledSlideObservations(this.activeCaseId, slideId);
    },
    getComments(slideId: string) {
      return this.slideComments(this.activeCaseId, slideId);
    },
    handleNavigateToSlide(payload: { slide: ISlide; alertIndex: number }) {
      this.closeModal();
      this.navigateToSlide({ slide: payload.slide, alertIndex: payload.alertIndex });
    },
    exportPDF() {
      this.$refs.contentToExport.save();
    },
    exportJSON() {
      let metaData = {
        productName: `Galen ${productName[this.systemMode]}`,
        versionNumber: this.globals.version.product,
        caseId: this.activeCaseId,
        tissue: this.activeCase.tissue,
        comments: this.caseReport.comments,
        completedBy: this.completedBy,
        completedOn: this.completedOn,
        isComplete: this.caseReport.isComplete,
      };
      const caseObservations = this.caseReport?.observations.map( (observation: any) => {
        return {
          key: observation.key,
          value: observation.value,
          title: observation.title,
        }
      }).filter( (observation: any) => {
        return observation.value
      })
      let slidesReports = {}
      Object.entries(this.caseReport?.slidesReports).forEach((slideReport: any) => {
        delete slideReport[1].isComplete
        slideReport[1].observations = slideReport[1].observations.filter( (observation: any) => {
          return !!observation.value
        })
        slidesReports[slideReport[0]] = slideReport[1];
      })
      let exportObj = {
        ...metaData,
        ...{observations: caseObservations },
        ...{slidesReports: slidesReports },
      };
      delete exportObj.isComplete
      let exportName = `case report ${this.activeCaseId} ${this.completedOn}`;
      var dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(exportObj));
      var downloadAnchorNode = document.createElement("a");
      downloadAnchorNode.setAttribute("href", dataStr);
      downloadAnchorNode.setAttribute("download", exportName + ".json");
      document.body.appendChild(downloadAnchorNode); // required for firefox
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
    },
    copyToClipboard() {
      //generating case level data as string
      //case title
      let caseReportString = `${this.t("reports.copyToClipboardText.caseId")}:\t${
        this.activeCaseId
      }\n`;
      //case observations
      let caseObservations = "";
      if (this.caseReport.observations.length > 0) {
        this.caseReport.observations.forEach((observation) => {
          if (observation.value && observation?.value.length && observation.title) {
            caseObservations += `${observation.title}\t ${observation.value}\n`;
          }
        });
      }
      caseReportString += caseObservations;
      //case comments
      if (this.caseReport.comments.length > 0) {
        caseReportString +=
          `${this.t("reports.copyToClipboardText.comments")}:\t` + this.caseReport.comments + "\n";
      }
      caseReportString += "\n";

      //generating slide level data as string for each lide in the current case
      let slideIds = Object.keys(this.caseReport.slidesReports);
      slideIds.forEach((slideId) => {
        //slide title
        let slide = `${this.t("reports.copyToClipboardText.slideId")}:\t${slideId}\n`;
        //slide observations
        let slideObservations = "";
        if (this.caseReport.slidesReports[slideId].observations.length > 0) {
          this.caseReport.slidesReports[slideId].observations.forEach((observation) => {
            if (observation.hasOwnProperty("value") && observation.value) {
              slideObservations += `${observation.title}\t ${observation.value}\n`;
            }
          });
          slide += slideObservations;
        }
        //slide comments
        if (this.caseReport.slidesReports[slideId].comments.value.length > 0) {
          slide +=
            `${this.t("reports.copyToClipboardText.comments")}:\t` +
            this.caseReport.slidesReports[slideId].comments.value +
            "\n";
        }
        slide += "\n";
        caseReportString += slide;
      });

      navigator.clipboard.writeText(caseReportString).then(() => {
        this.isCopied = true;
        this.copyToClipboardText = "reports.actionButtonText.copied";
        setTimeout(() => {
          this.copyToClipboardText = "reports.actionButtonText.copyToClipboard";
          this.isCopied = false;
        }, 2000);
      });
    },

    checkValidity(): void {
      this.isFormValid = (this.$refs.caseReportForm as HTMLFormElement).checkValidity();
    },
    reportValidity(): void {
      this.isFormValid = (this.$refs.caseReportForm as HTMLFormElement).reportValidity();
    },
  },
});
